import React, { useState, useEffect } from 'react';
import { Menu, X, Mail, Phone } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useTranslation } from '../hooks/useTranslation';
import { businessInfo } from '../data/mock';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // If it's a route (like blog), navigate to it
    const navItem = navItems.find(item => item.id === sectionId);
    if (navItem && navItem.isRoute) {
      navigate(`/${sectionId}`);
      setIsMobileMenuOpen(false);
      return;
    }

    // Otherwise scroll to section on home page
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { label: t.header.nav.home, id: 'home' },
    { label: t.header.nav.about, id: 'about' },
    { label: t.header.nav.services, id: 'services' },
    { label: t.header.nav.blog, id: 'blog', isRoute: true },
    { label: t.header.nav.contact, id: 'contact' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
    }`}>
      {/* Top bar */}
      <div className={`border-b transition-colors ${
        isScrolled ? 'border-gray-200 bg-navy-900' : 'bg-navy-900 border-navy-800'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-end gap-6 py-2 text-sm">
            <a href="mailto:mail.varaskar@yahoo.in" className="flex items-center gap-2 text-white/90 hover:text-white transition-colors">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">{businessInfo.email}</span>
            </a>
            <a href="tel:+919423434146" className="flex items-center gap-2 text-white/90 hover:text-white transition-colors">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div 
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-full bg-gradient-to-br from-burgundy-600 to-burgundy-800 flex items-center justify-center cursor-pointer hover:scale-105 transition-transform"
            >
              <span className="text-white font-bold text-lg">RV</span>
            </div>
            <div 
              onClick={() => navigate('/')}
              className="cursor-pointer"
            >
              <h1 className={`font-bold text-xl transition-colors ${
                isScrolled ? 'text-navy-900' : 'text-white'
              }`}>
                {t.header.title}
              </h1>
              <p className={`text-xs transition-colors ${
                isScrolled ? 'text-gray-600' : 'text-white/80'
              }`}>
                {t.header.subtitle}
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`font-medium transition-colors hover:text-burgundy-600 ${
                  isScrolled ? 'text-navy-900' : 'text-white'
                }`}
              >
                {item.label}
              </button>
            ))}
            <LanguageSwitcher variant={isScrolled ? "outline" : "ghost"} />
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-burgundy-700 hover:bg-burgundy-800 text-white"
            >
              {t.header.cta}
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 transition-colors ${
              isScrolled ? 'text-navy-900' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden pb-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`block w-full text-left px-4 py-2 rounded-lg transition-colors ${
                  isScrolled 
                    ? 'text-navy-900 hover:bg-gray-100' 
                    : 'text-white hover:bg-white/10'
                }`}
              >
                {item.label}
              </button>
            ))}
            <div className="px-4 py-2">
              <LanguageSwitcher variant={isScrolled ? "outline" : "ghost"} />
            </div>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="w-full bg-burgundy-700 hover:bg-burgundy-800 text-white"
            >
              {t.header.cta}
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
};
